import { Controller } from "@hotwired/stimulus"
import * as fabric from "fabric"

export default class extends Controller {
  static targets = [
    "canvas", "canvasWrapper", "form", "canvasJsonField", "pngDataField",
    "fontSelect", "fontSizeSelect", "colorPicker", "highlightPicker",
    "boldBtn", "italicBtn", "underlineBtn", "strikeBtn",
    "alignLeftBtn", "alignCenterBtn", "alignRightBtn",
    "outlineToggle", "outlineWidth", "outlineColor",
    "shadowToggle", "shadowBlur", "shadowOffsetX", "shadowOffsetY", "shadowColor",
    "opacitySlider", "opacityValue",
    "boxBorderToggle", "boxBorderWidth", "boxBorderColor", "boxBorderPadding",
    "clipBorderToggle", "clipBorderWidth", "clipBorderColor",
    "bgColorPicker", "moreToggle", "advancedBar",
    "imageMenuBtn", "imageInsertPanel", "insertImageUrl", "insertImageUpload"
  ]

  static values = {
    imageUrl: String,
    proxyUrl: String,
    clientName: String,
    canvasJson: String
  }

  CANVAS_WIDTH = 1080
  CANVAS_HEIGHT = 1920

  connect() {
    this.fabricCanvas = null
    this.history = []
    this.historyIndex = -1
    this.skipHistory = false
    this._clipBorderEnabled = false
    this._initCanvas()
  }

  disconnect() {
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler)
      this._resizeHandler = null
    }
    if (this.fabricCanvas) {
      this.fabricCanvas.dispose()
      this.fabricCanvas = null
    }
  }

  // Canvas initialization
  async _initCanvas() {
    const canvasEl = this.canvasTarget

    this.fabricCanvas = new fabric.Canvas(canvasEl, {
      width: this.CANVAS_WIDTH,
      height: this.CANVAS_HEIGHT,
      backgroundColor: "#ffffff",
      preserveObjectStacking: true,
      selection: true
    })

    this.fabricCanvas.on("after:render", () => this._drawClipBorder())
    this.fabricCanvas.on("selection:created", (e) => this._syncToolbar(e.selected?.[0]))
    this.fabricCanvas.on("selection:updated", (e) => this._syncToolbar(e.selected?.[0]))
    this.fabricCanvas.on("selection:cleared", () => this._clearToolbar())

    // Extend Textbox render for box border support
    const origTextRender = fabric.Textbox.prototype._render
    fabric.Textbox.prototype._render = function(ctx) {
      origTextRender.call(this, ctx)
      if (this._boxBorderWidth && this._boxBorderWidth > 0 && this._boxBorderColor) {
        ctx.save()
        const pad = this._boxBorderPadding || 10
        const w = this.width + pad * 2
        const h = this.height + pad * 2
        ctx.strokeStyle = this._boxBorderColor
        ctx.lineWidth = this._boxBorderWidth
        ctx.strokeRect(-this.width / 2 - pad, -this.height / 2 - pad, w, h)
        ctx.restore()
      }
    }

    // Extend Image render for box border support
    const origImgRender = fabric.FabricImage.prototype._render
    fabric.FabricImage.prototype._render = function(ctx) {
      origImgRender.call(this, ctx)
      if (this._boxBorderWidth && this._boxBorderWidth > 0 && this._boxBorderColor) {
        ctx.save()
        const pad = this._boxBorderPadding || 10
        const w = this.width + pad * 2
        const h = this.height + pad * 2
        ctx.strokeStyle = this._boxBorderColor
        ctx.lineWidth = this._boxBorderWidth / (this.scaleX || 1)
        ctx.strokeRect(-this.width / 2 - pad, -this.height / 2 - pad, w, h)
        ctx.restore()
      }
    }

    this._fitToScreen()
    this._resizeHandler = () => this._fitToScreen()
    window.addEventListener("resize", this._resizeHandler)

    // Schedule additional zoom fixes
    setTimeout(() => this._fitToScreen(), 100)
    setTimeout(() => this._fitToScreen(), 300)
    setTimeout(() => this._fitToScreen(), 600)

    if (this.canvasJsonValue && this.canvasJsonValue.length > 2) {
      // Editing existing: restore saved state
      await this.fabricCanvas.loadFromJSON(this.canvasJsonValue)
      this.fabricCanvas.renderAll()
      this._saveHistory()
    } else if (this.imageUrlValue) {
      // New: load the original clip image
      await this._loadClipImage()
    }
  }

  _fitToScreen() {
    if (!this.fabricCanvas) return
    const wrapper = this.canvasWrapperTarget
    if (!wrapper) return

    // Calculate max available height (viewport minus header/toolbar/buttons)
    const maxH = window.innerHeight - 240
    const maxW = wrapper.parentElement?.offsetWidth || window.innerWidth - 40

    // Zoom to fit the 9:16 canvas in available space
    const zoomH = maxH / this.CANVAS_HEIGHT
    const zoomW = maxW / this.CANVAS_WIDTH
    const zoom = Math.min(zoomH, zoomW, 1)

    this.fabricCanvas.setZoom(zoom)
    this.fabricCanvas.setDimensions({
      width: this.CANVAS_WIDTH * zoom,
      height: this.CANVAS_HEIGHT * zoom
    })
    this.fabricCanvas.calcOffset()
  }

  async _loadClipImage() {
    const proxyUrl = `${this.proxyUrlValue}?url=${encodeURIComponent(this.imageUrlValue)}`
    try {
      const img = await fabric.FabricImage.fromURL(proxyUrl, { crossOrigin: "anonymous" })
      if (!img || !img.width) return

      const scale = this.CANVAS_WIDTH / img.width
      img.set({
        left: this.CANVAS_WIDTH / 2,
        top: this.CANVAS_HEIGHT / 3,
        originX: "center",
        originY: "center",
        scaleX: scale,
        scaleY: scale,
        selectable: true,
        evented: true,
        cornerColor: "rgba(196, 164, 132, 0.9)",
        cornerStyle: "circle",
        cornerSize: 12,
        transparentCorners: false,
        borderColor: "rgba(196, 164, 132, 0.7)",
        name: "clip-image"
      })
      this.fabricCanvas.add(img)
      this.fabricCanvas.setActiveObject(img)
      this.fabricCanvas.renderAll()
      this._saveHistory()
    } catch (e) {
      console.error("Failed to load clip image:", e)
    }
  }

  // Toolbar sync
  _syncToolbar(obj) {
    if (!obj || !obj.selectable) { this._clearToolbar(); return }

    const isText = obj.type === "textbox" || obj.type === "i-text"
    const isImage = obj.type === "image"

    if (!isText && !isImage) { this._clearToolbar(); return }

    if (isText) {
      if (this.hasFontSelectTarget) this.fontSelectTarget.value = obj.fontFamily || "Inter"
      if (this.hasFontSizeSelectTarget) this.fontSizeSelectTarget.value = String(Math.round(obj.fontSize || 24))
      if (this.hasColorPickerTarget) this.colorPickerTarget.value = this._toHex(obj.fill) || "#ffffff"
      if (this.hasBoldBtnTarget) this.boldBtnTarget.classList.toggle("active", obj.fontWeight === "700" || obj.fontWeight === "bold")
      if (this.hasItalicBtnTarget) this.italicBtnTarget.classList.toggle("active", obj.fontStyle === "italic")
      if (this.hasUnderlineBtnTarget) this.underlineBtnTarget.classList.toggle("active", !!obj.underline)
      if (this.hasStrikeBtnTarget) this.strikeBtnTarget.classList.toggle("active", !!obj.linethrough)

      const align = obj.textAlign || "left"
      if (this.hasAlignLeftBtnTarget) this.alignLeftBtnTarget.classList.toggle("active", align === "left")
      if (this.hasAlignCenterBtnTarget) this.alignCenterBtnTarget.classList.toggle("active", align === "center")
      if (this.hasAlignRightBtnTarget) this.alignRightBtnTarget.classList.toggle("active", align === "right")

      if (this.hasHighlightPickerTarget) this.highlightPickerTarget.value = this._toHex(obj.backgroundColor) || "#000000"
    }

    // Shared controls
    const hasBox = obj._boxBorderWidth && obj._boxBorderWidth > 0
    if (this.hasBoxBorderToggleTarget) this.boxBorderToggleTarget.classList.toggle("active", hasBox)
    if (this.hasBoxBorderWidthTarget) this.boxBorderWidthTarget.value = obj._boxBorderWidth || 3
    if (this.hasBoxBorderColorTarget) this.boxBorderColorTarget.value = obj._boxBorderColor || "#ffffff"
    if (this.hasBoxBorderPaddingTarget) this.boxBorderPaddingTarget.value = obj._boxBorderPadding || 10

    if (this.hasOutlineToggleTarget) this.outlineToggleTarget.classList.toggle("active", !!obj.stroke && obj.strokeWidth > 0)
    if (this.hasOutlineWidthTarget) this.outlineWidthTarget.value = obj.strokeWidth || 2
    if (this.hasOutlineColorTarget) this.outlineColorTarget.value = this._toHex(obj.stroke) || "#000000"

    const sh = obj.shadow
    if (this.hasShadowToggleTarget) this.shadowToggleTarget.classList.toggle("active", !!sh)
    if (sh) {
      if (this.hasShadowBlurTarget) this.shadowBlurTarget.value = sh.blur || 15
      if (this.hasShadowOffsetXTarget) this.shadowOffsetXTarget.value = sh.offsetX || 6
      if (this.hasShadowOffsetYTarget) this.shadowOffsetYTarget.value = sh.offsetY || 6
      if (this.hasShadowColorTarget) this.shadowColorTarget.value = this._toHex(sh.color) || "#000000"
    }

    if (this.hasOpacitySliderTarget) this.opacitySliderTarget.value = Math.round((obj.opacity || 1) * 100)
    if (this.hasOpacityValueTarget) this.opacityValueTarget.textContent = Math.round((obj.opacity || 1) * 100) + "%"
  }

  _clearToolbar() {
    ;["boldBtn", "italicBtn", "underlineBtn", "strikeBtn",
      "alignLeftBtn", "alignCenterBtn", "alignRightBtn", "outlineToggle", "shadowToggle", "boxBorderToggle"
    ].forEach(name => {
      const hasTarget = `has${name.charAt(0).toUpperCase() + name.slice(1)}Target`
      if (this[hasTarget]) this[`${name}Target`].classList.remove("active")
    })
  }

  // Text helpers
  _getTextObj() {
    const obj = this.fabricCanvas?.getActiveObject()
    return (obj && (obj.type === "textbox" || obj.type === "i-text")) ? obj : null
  }

  _getAnyObj() { return this.fabricCanvas?.getActiveObject() }

  _getStylableObj() {
    const obj = this.fabricCanvas?.getActiveObject()
    if (!obj || !obj.selectable) return null
    if (obj.type === "textbox" || obj.type === "i-text" || obj.type === "image") return obj
    return null
  }

  // Text formatting
  changeFont(e) { const o = this._getTextObj(); if (o) { o.set("fontFamily", e.target.value); this.fabricCanvas.renderAll(); this._saveHistory() } }
  changeFontSize(e) { const o = this._getTextObj(); if (o) { o.set("fontSize", parseInt(e.target.value) || 24); this.fabricCanvas.renderAll(); this._saveHistory() } }
  changeColor(e) { const o = this._getTextObj(); if (o) { o.set("fill", e.target.value); this.fabricCanvas.renderAll(); this._saveHistory() } }
  changeHighlight(e) { const o = this._getTextObj(); if (o) { o.set("backgroundColor", e.target.value); this.fabricCanvas.renderAll(); this._saveHistory() } }

  toggleBold() {
    const o = this._getTextObj(); if (!o) return
    const now = o.fontWeight === "700" || o.fontWeight === "bold"
    o.set("fontWeight", now ? "400" : "700")
    if (this.hasBoldBtnTarget) this.boldBtnTarget.classList.toggle("active", !now)
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  toggleItalic() {
    const o = this._getTextObj(); if (!o) return
    const now = o.fontStyle === "italic"
    o.set("fontStyle", now ? "normal" : "italic")
    if (this.hasItalicBtnTarget) this.italicBtnTarget.classList.toggle("active", !now)
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  toggleUnderline() {
    const o = this._getTextObj(); if (!o) return
    o.set("underline", !o.underline)
    if (this.hasUnderlineBtnTarget) this.underlineBtnTarget.classList.toggle("active", o.underline)
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  toggleStrike() {
    const o = this._getTextObj(); if (!o) return
    o.set("linethrough", !o.linethrough)
    if (this.hasStrikeBtnTarget) this.strikeBtnTarget.classList.toggle("active", o.linethrough)
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  setAlign(e) {
    const o = this._getTextObj(); if (!o) return
    const align = e.currentTarget.dataset.align || "left"
    o.set("textAlign", align)
    if (this.hasAlignLeftBtnTarget) this.alignLeftBtnTarget.classList.toggle("active", align === "left")
    if (this.hasAlignCenterBtnTarget) this.alignCenterBtnTarget.classList.toggle("active", align === "center")
    if (this.hasAlignRightBtnTarget) this.alignRightBtnTarget.classList.toggle("active", align === "right")
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  // Outline (text stroke)
  toggleOutline() {
    const o = this._getStylableObj(); if (!o) return
    const hasOutline = !!o.stroke && o.strokeWidth > 0
    if (hasOutline) {
      o.set({ stroke: null, strokeWidth: 0 })
    } else {
      o.set({
        stroke: this.hasOutlineColorTarget ? this.outlineColorTarget.value : "#000000",
        strokeWidth: this.hasOutlineWidthTarget ? parseInt(this.outlineWidthTarget.value) || 2 : 2,
        strokeUniform: true,
        paintFirst: "stroke"
      })
    }
    if (this.hasOutlineToggleTarget) this.outlineToggleTarget.classList.toggle("active", !hasOutline)
    o.dirty = true
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  changeOutlineWidth(e) {
    const o = this._getStylableObj()
    if (o && o.stroke) { o.set("strokeWidth", parseInt(e.target.value) || 1); o.dirty = true; this.fabricCanvas.renderAll(); this._saveHistory() }
  }

  changeOutlineColor(e) {
    const o = this._getStylableObj()
    if (o && o.stroke) { o.set("stroke", e.target.value); o.dirty = true; this.fabricCanvas.renderAll(); this._saveHistory() }
  }

  // Box border
  toggleBoxBorder() {
    const o = this._getStylableObj(); if (!o) return
    const has = o._boxBorderWidth && o._boxBorderWidth > 0
    if (has) {
      o._boxBorderWidth = 0
    } else {
      o._boxBorderWidth = this.hasBoxBorderWidthTarget ? parseInt(this.boxBorderWidthTarget.value) || 3 : 3
      o._boxBorderColor = this.hasBoxBorderColorTarget ? this.boxBorderColorTarget.value : "#ffffff"
      o._boxBorderPadding = this.hasBoxBorderPaddingTarget ? parseInt(this.boxBorderPaddingTarget.value) || 10 : 10
    }
    if (this.hasBoxBorderToggleTarget) this.boxBorderToggleTarget.classList.toggle("active", !has)
    o.dirty = true; this.fabricCanvas.renderAll(); this._saveHistory()
  }

  changeBoxBorderWidth(e) {
    const o = this._getStylableObj()
    if (o && o._boxBorderWidth > 0) { o._boxBorderWidth = parseInt(e.target.value) || 1; o.dirty = true; this.fabricCanvas.renderAll(); this._saveHistory() }
  }

  changeBoxBorderColor(e) {
    const o = this._getStylableObj()
    if (o && o._boxBorderWidth > 0) { o._boxBorderColor = e.target.value; o.dirty = true; this.fabricCanvas.renderAll(); this._saveHistory() }
  }

  changeBoxBorderPadding(e) {
    const o = this._getStylableObj()
    if (o && o._boxBorderWidth > 0) { o._boxBorderPadding = parseInt(e.target.value) || 0; o.dirty = true; this.fabricCanvas.renderAll(); this._saveHistory() }
  }

  // Shadow
  toggleShadow() {
    const o = this._getStylableObj(); if (!o) return
    if (o.shadow) {
      o.set("shadow", null)
    } else {
      o.set("shadow", new fabric.Shadow({
        color: this.hasShadowColorTarget ? this.shadowColorTarget.value : "rgba(0,0,0,0.8)",
        blur: this.hasShadowBlurTarget ? parseInt(this.shadowBlurTarget.value) || 15 : 15,
        offsetX: this.hasShadowOffsetXTarget ? parseInt(this.shadowOffsetXTarget.value) || 6 : 6,
        offsetY: this.hasShadowOffsetYTarget ? parseInt(this.shadowOffsetYTarget.value) || 6 : 6
      }))
    }
    if (this.hasShadowToggleTarget) this.shadowToggleTarget.classList.toggle("active", !!o.shadow)
    o.dirty = true; this.fabricCanvas.renderAll(); this._saveHistory()
  }

  changeShadowBlur(e) { this._updShadow("blur", parseInt(e.target.value)) }
  changeShadowOffsetX(e) { this._updShadow("offsetX", parseInt(e.target.value)) }
  changeShadowOffsetY(e) { this._updShadow("offsetY", parseInt(e.target.value)) }
  changeShadowColor(e) { this._updShadow("color", e.target.value) }

  _updShadow(prop, val) {
    const o = this._getStylableObj()
    if (!o) return
    if (!o.shadow) {
      o.set("shadow", new fabric.Shadow({
        color: this.hasShadowColorTarget ? this.shadowColorTarget.value : "rgba(0,0,0,0.8)",
        blur: 15, offsetX: 6, offsetY: 6
      }))
      if (this.hasShadowToggleTarget) this.shadowToggleTarget.classList.add("active")
    }
    o.shadow[prop] = val
    o.dirty = true; this.fabricCanvas.renderAll(); this._saveHistory()
  }

  // Opacity
  changeOpacity(e) {
    const o = this._getAnyObj(); if (!o || !o.selectable) return
    const val = parseInt(e.target.value) / 100
    o.set("opacity", val)
    if (this.hasOpacityValueTarget) this.opacityValueTarget.textContent = e.target.value + "%"
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  // Background color
  setBgWhite() { this.fabricCanvas.backgroundColor = "#ffffff"; this.fabricCanvas.renderAll(); this._saveHistory() }
  setBgBlack() { this.fabricCanvas.backgroundColor = "#000000"; this.fabricCanvas.renderAll(); this._saveHistory() }
  changeBgColor(e) { this.fabricCanvas.backgroundColor = e.target.value; this.fabricCanvas.renderAll(); this._saveHistory() }

  // Clip border (canvas edge border)
  toggleClipBorder() {
    this._clipBorderEnabled = !this._clipBorderEnabled
    if (this.hasClipBorderToggleTarget) this.clipBorderToggleTarget.classList.toggle("active", this._clipBorderEnabled)
    this.fabricCanvas.renderAll(); this._saveHistory()
  }

  changeClipBorderWidth() { if (this._clipBorderEnabled) this.fabricCanvas.renderAll() }
  changeClipBorderColor() { if (this._clipBorderEnabled) this.fabricCanvas.renderAll() }

  _drawClipBorder() {
    if (!this._clipBorderEnabled || !this.fabricCanvas) return
    const ctx = this.fabricCanvas.getContext("2d")
    const zoom = this.fabricCanvas.getZoom()
    const width = this.hasClipBorderWidthTarget ? parseInt(this.clipBorderWidthTarget.value) || 20 : 20
    const color = this.hasClipBorderColorTarget ? this.clipBorderColorTarget.value : "#1a1a1a"
    const scaledWidth = width * zoom
    const canvasW = this.CANVAS_WIDTH * zoom
    const canvasH = this.CANVAS_HEIGHT * zoom
    ctx.save()
    ctx.strokeStyle = color
    ctx.lineWidth = scaledWidth
    ctx.strokeRect(scaledWidth / 2, scaledWidth / 2, canvasW - scaledWidth, canvasH - scaledWidth)
    ctx.restore()
  }

  // Layer controls
  bringForward() { const o = this._getAnyObj(); if (o && o.selectable) { this.fabricCanvas.bringObjectForward(o); this.fabricCanvas.renderAll() } }
  sendBackward() { const o = this._getAnyObj(); if (o && o.selectable) { this.fabricCanvas.sendObjectBackwards(o); this.fabricCanvas.renderAll() } }

  duplicateSelected() {
    const o = this._getAnyObj()
    if (!o || !o.selectable) return
    o.clone().then(cloned => {
      cloned.set({ left: o.left + 30, top: o.top + 30 })
      this.fabricCanvas.add(cloned)
      this.fabricCanvas.setActiveObject(cloned)
      this.fabricCanvas.renderAll()
      this._saveHistory()
    })
  }

  addText() {
    const text = new fabric.Textbox("New Text", {
      left: this.CANVAS_WIDTH / 2 - 200,
      top: this.CANVAS_HEIGHT / 2,
      width: 400,
      fontFamily: "Inter",
      fontSize: 48,
      fill: "#ffffff",
      editable: true,
      selectable: true,
      evented: true,
      cornerColor: "rgba(196, 164, 132, 0.9)",
      cornerStyle: "circle",
      cornerSize: 12,
      transparentCorners: false,
      borderColor: "rgba(196, 164, 132, 0.7)"
    })
    this.fabricCanvas.add(text)
    this.fabricCanvas.setActiveObject(text)
    this.fabricCanvas.renderAll()
    this._saveHistory()
  }

  deleteSelected() {
    const o = this._getAnyObj()
    if (o && o.selectable) {
      this.fabricCanvas.remove(o)
      this.fabricCanvas.discardActiveObject()
      this.fabricCanvas.renderAll()
      this._saveHistory()
    }
  }

  resetCanvas() {
    this.fabricCanvas.clear()
    this.fabricCanvas.backgroundColor = "#ffffff"
    this.history = []
    this.historyIndex = -1
    this._clipBorderEnabled = false
    if (this.hasClipBorderToggleTarget) this.clipBorderToggleTarget.classList.remove("active")
    if (this.imageUrlValue) {
      this._loadClipImage()
    } else {
      this.fabricCanvas.renderAll()
    }
  }

  // Toggle advanced bar
  toggleMore() {
    if (this.hasAdvancedBarTarget) {
      const isHidden = this.advancedBarTarget.classList.toggle("hidden")
      if (this.hasMoreToggleTarget) this.moreToggleTarget.classList.toggle("active", !isHidden)
      if (this.hasImageInsertPanelTarget) {
        if (isHidden) {
          this.imageInsertPanelTarget.classList.add("hidden")
          if (this.hasImageMenuBtnTarget) this.imageMenuBtnTarget.classList.remove("active")
        } else {
          this.imageInsertPanelTarget.classList.remove("hidden")
          if (this.hasImageMenuBtnTarget) this.imageMenuBtnTarget.classList.add("active")
        }
      }
      // Update Row 1 border radius
      const row1 = this.advancedBarTarget.previousElementSibling
      if (row1) {
        if (isHidden) { row1.classList.remove("rounded-t-xl"); row1.classList.add("rounded-xl") }
        else { row1.classList.remove("rounded-xl"); row1.classList.add("rounded-t-xl") }
      }
    }
  }

  // Image insertion
  toggleImageMenu() {
    if (!this.hasImageInsertPanelTarget) return
    const isHidden = this.imageInsertPanelTarget.classList.toggle("hidden")
    if (this.hasImageMenuBtnTarget) this.imageMenuBtnTarget.classList.toggle("active", !isHidden)
    if (this.hasAdvancedBarTarget) {
      if (isHidden) this.advancedBarTarget.classList.add("rounded-b-xl")
      else this.advancedBarTarget.classList.remove("rounded-b-xl")
    }
  }

  triggerImageUpload() { if (this.hasInsertImageUploadTarget) this.insertImageUploadTarget.click() }

  uploadImageFile(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => this._insertImageOnCanvas(e.target.result)
    reader.readAsDataURL(file)
    event.target.value = ""
  }

  insertImageFromUrl() {
    if (!this.hasInsertImageUrlTarget) return
    const url = this.insertImageUrlTarget.value.trim()
    if (!url) return
    const proxiedUrl = this.proxyUrlValue ? `${this.proxyUrlValue}?url=${encodeURIComponent(url)}` : url
    this._insertImageOnCanvas(proxiedUrl)
    this.insertImageUrlTarget.value = ""
  }

  async _insertImageOnCanvas(url) {
    if (!this.fabricCanvas) return
    try {
      const img = await fabric.FabricImage.fromURL(url, { crossOrigin: "anonymous" })
      if (!img || !img.width) return
      const maxDim = this.CANVAS_WIDTH * 0.4
      const scale = Math.min(maxDim / img.width, maxDim / img.height, 1)
      img.set({
        scaleX: scale, scaleY: scale,
        left: this.CANVAS_WIDTH / 2, top: this.CANVAS_HEIGHT / 2,
        originX: "center", originY: "center",
        selectable: true, evented: true,
        cornerColor: "rgba(196, 164, 132, 0.9)",
        cornerStyle: "circle", cornerSize: 12,
        transparentCorners: false,
        borderColor: "rgba(196, 164, 132, 0.7)",
        name: "inserted-image",
        strokeUniform: true, paintFirst: "stroke"
      })
      this.fabricCanvas.add(img)
      this.fabricCanvas.setActiveObject(img)
      this.fabricCanvas.renderAll()
      this._saveHistory()
    } catch (e) {
      console.error("Failed to insert image:", e)
    }
  }

  // Drag and drop for image insertion
  imageDropOver(event) {
    event.preventDefault(); event.stopPropagation()
    if (this.hasImageInsertPanelTarget) this.imageInsertPanelTarget.classList.add("ring-2", "ring-[#C4A484]", "bg-[#C4A484]/5")
  }

  imageDropLeave(event) {
    event.preventDefault(); event.stopPropagation()
    if (this.hasImageInsertPanelTarget) this.imageInsertPanelTarget.classList.remove("ring-2", "ring-[#C4A484]", "bg-[#C4A484]/5")
  }

  handleImageDrop(event) {
    event.preventDefault(); event.stopPropagation()
    if (this.hasImageInsertPanelTarget) this.imageInsertPanelTarget.classList.remove("ring-2", "ring-[#C4A484]", "bg-[#C4A484]/5")
    const files = event.dataTransfer?.files
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file.type.startsWith("image/")) return
    const reader = new FileReader()
    reader.onload = (e) => this._insertImageOnCanvas(e.target.result)
    reader.readAsDataURL(file)
  }

  // Undo/Redo
  _saveHistory() {
    if (this.skipHistory) return
    const json = JSON.stringify(this.fabricCanvas.toJSON([
      "name", "selectable", "evented", "editable",
      "_boxBorderWidth", "_boxBorderColor", "_boxBorderPadding"
    ]))
    if (this.historyIndex < this.history.length - 1) {
      this.history = this.history.slice(0, this.historyIndex + 1)
    }
    this.history.push(json)
    if (this.history.length > 30) this.history.shift()
    this.historyIndex = this.history.length - 1
  }

  undo() {
    if (this.historyIndex <= 0) return
    this.historyIndex--
    this._loadHistoryState(this.history[this.historyIndex])
  }

  redo() {
    if (this.historyIndex >= this.history.length - 1) return
    this.historyIndex++
    this._loadHistoryState(this.history[this.historyIndex])
  }

  async _loadHistoryState(json) {
    this.skipHistory = true
    await this.fabricCanvas.loadFromJSON(json)
    this.fabricCanvas.renderAll()
    this.skipHistory = false
  }

  // Save (submit form to server)
  save() {
    this.fabricCanvas.discardActiveObject()

    // Export at full resolution
    const currentZoom = this.fabricCanvas.getZoom()
    this.fabricCanvas.setZoom(1)
    this.fabricCanvas.setDimensions({ width: this.CANVAS_WIDTH, height: this.CANVAS_HEIGHT })
    this.fabricCanvas.renderAll()

    const canvasJson = JSON.stringify(this.fabricCanvas.toJSON([
      "name", "selectable", "evented", "editable",
      "_boxBorderWidth", "_boxBorderColor", "_boxBorderPadding"
    ]))
    const dataURL = this.canvasTarget.toDataURL("image/png")

    // Restore zoom
    this.fabricCanvas.setZoom(currentZoom)
    this.fabricCanvas.setDimensions({
      width: this.CANVAS_WIDTH * currentZoom,
      height: this.CANVAS_HEIGHT * currentZoom
    })
    this.fabricCanvas.renderAll()

    this.canvasJsonFieldTarget.value = canvasJson
    this.pngDataFieldTarget.value = dataURL
    this.formTarget.requestSubmit()
  }

  // Download (client-side, instant)
  download() {
    this.fabricCanvas.discardActiveObject()

    const currentZoom = this.fabricCanvas.getZoom()
    this.fabricCanvas.setZoom(1)
    this.fabricCanvas.setDimensions({ width: this.CANVAS_WIDTH, height: this.CANVAS_HEIGHT })
    this.fabricCanvas.renderAll()

    const dataURL = this.canvasTarget.toDataURL("image/png")

    this.fabricCanvas.setZoom(currentZoom)
    this.fabricCanvas.setDimensions({
      width: this.CANVAS_WIDTH * currentZoom,
      height: this.CANVAS_HEIGHT * currentZoom
    })
    this.fabricCanvas.renderAll()

    const link = document.createElement("a")
    link.download = `${this.clientNameValue || "story"}_story.png`
    link.href = dataURL
    link.click()
  }

  // Helpers
  _toHex(color) {
    if (!color) return null
    if (color.startsWith("#")) return color.substring(0, 7)
    if (color.startsWith("rgb")) {
      const m = color.match(/(\d+)/g)
      if (m && m.length >= 3) return "#" + m.slice(0, 3).map(n => parseInt(n).toString(16).padStart(2, "0")).join("")
    }
    return color
  }
}
